import { t } from "@lingui/core/macro";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { cn } from "@/utils";
import {
  type CompensationChoice,
  draftForChoice,
  formatCompensation,
} from "../../TimeOffTypes/timeOffCompensation";
import type { TimeOffFormState } from "./useTimeOffForm";

function percentLabel(percent: number): string {
  return percent === 0 ? t`Neplačano (0 %)` : t`${percent} % urnega stroška`;
}

/** One field like "Ponavljanje": the type's rate first, then the salon's own percentages, then custom. */
export const TimeOffCompensationSelect = ({
  state,
}: {
  state: TimeOffFormState;
}) => {
  const { form, values } = state;
  const choice = values.compensationChoice;
  // An opened override may hold a percentage no type uses; it still has to be selectable.
  const openedPercent = choice.startsWith("percent:")
    ? Number(choice.slice("percent:".length))
    : null;
  const percents = [
    ...new Set([
      ...state.compensationPresets,
      ...(openedPercent === null ? [] : [openedPercent]),
    ]),
  ].sort((a, b) => b - a);

  const items = [
    {
      key: "inherit",
      label: t`Kot pri vrsti · ${formatCompensation(state.typeCompensation)}`,
    },
    ...percents.map((percent) => ({
      key: `percent:${percent}`,
      label: percentLabel(percent),
    })),
    { key: "customPercent", label: t`Drug odstotek…` },
    { key: "flatPerDay", label: t`Znesek na dan…` },
  ];

  function choose(next: CompensationChoice) {
    form.setValues({
      compensationChoice: next,
      compensation: draftForChoice(next, values.compensation),
    });
  }

  const error = state.errorFor("compensation");
  const hasValue = choice === "customPercent" || choice === "flatPerDay";

  return (
    <div
      className={cn(
        "grid w-full items-start gap-2",
        hasValue ? "grid-cols-[minmax(0,1fr)_8rem]" : "grid-cols-1",
      )}
    >
      <div className="min-w-0">
        <LimeSelect
          label={t`Nadomestilo`}
          data-identifier="time-off-compensation"
          disallowEmptySelection
          items={items}
          value={choice}
          onSingleItemChange={(value) => choose(value as CompensationChoice)}
        />
      </div>
      {choice === "customPercent" && (
        <LimeInput
          label={t`Odstotek`}
          value={values.compensation.percent}
          onValueChange={(percent) =>
            form.setFieldValue("compensation", {
              ...values.compensation,
              percent,
            })
          }
          inputMode="decimal"
          endContent={<span className="text-default-400 text-small">%</span>}
          error={error}
          autoFocus
        />
      )}
      {choice === "flatPerDay" && (
        <LimeInput
          label={t`Na dan`}
          placeholder="10,00"
          value={values.compensation.flatAmount}
          onValueChange={(flatAmount) =>
            form.setFieldValue("compensation", {
              ...values.compensation,
              flatAmount,
            })
          }
          inputMode="decimal"
          endContent={<span className="text-default-400 text-small">€</span>}
          error={error}
          autoFocus
        />
      )}
    </div>
  );
};
